import React, { useEffect, useState, useCallback } from 'react';
import { useDisplayName, useSignupMethod, useSetOnboardingStage, useSetLinkId, useStripeOnboardingStatus } from '../state';
import fetchWithToken, { updateOnboardingStage } from '../services';
import { useNavigate } from 'react-router-dom';
import Loading from './Loading';
import { AUTH_ROUTES, ONBOARDING_ROUTES, GENERAL_ROUTES } from '../constants/routes';
import { FaCheckCircle } from 'react-icons/fa';
import { FaExclamationCircle } from 'react-icons/fa';

const EnterLinkId = () => {
    const displayName = useDisplayName();
    const signupMethod = useSignupMethod();
    const setOnboardingStage = useSetOnboardingStage();
    const setLinkId = useSetLinkId();
    const stripeOnboardingStatus = useStripeOnboardingStatus();
    const [isLoading, setIsLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [placeholderLinkId, setPlaceholderLinkId] = useState('');
    const [isValid, setIsValid] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    // Function to format a name into a valid link ID
    const formatName = (input) => {
        let formatted = input.replace(/\s+/g, ' ').trim();
        formatted = formatted.replace(/[^a-zA-Z0-9\s-]/g, '')
            .replace(/\s/g, '-')
            .toLowerCase();
        formatted = formatted.replace(/-+/g, '-');
        return formatted;
    };

    // Check for existing linkId or generate a placeholder link ID on component mount
    useEffect(() => {
        const checkOrGeneratePlaceholderLinkId = async () => {
            if (!displayName) {
                navigate(ONBOARDING_ROUTES.ENTER_DISPLAY_NAME);
                return;
            }

            try {
                // First, check if the user already has a linkId
                const userResponse = await fetchWithToken('/user?fields=linkId', {
                    method: 'GET',
                });

                const userData = await userResponse.json();

                // If user already has a linkId, use it as the placeholder
                if (userData.linkId) {
                    setPlaceholderLinkId(userData.linkId);
                    setInputValue(userData.linkId);
                    setIsValid(true);
                    setIsLoading(false);
                    return; // Exit early since we found an existing linkId
                }

                // Otherwise, generate a new placeholder based on display name
                const formattedName = formatName(displayName);
                let linkId = formattedName;

                // Check if this linkId already exists
                const response = await fetchWithToken('/check-link-unique', {
                    method: 'POST',
                    body: JSON.stringify({ linkId }),
                });

                const data = await response.json();

                if (!data.isUnique) {
                    // If not unique, try with a counter
                    let counter = 1;
                    let isUnique = false;

                    while (!isUnique && counter < 1000) {
                        linkId = `${formattedName}-${counter}`;

                        const checkResponse = await fetchWithToken('/check-link-unique', {
                            method: 'POST',
                            body: JSON.stringify({ linkId }),
                        });

                        const checkData = await checkResponse.json();

                        if (checkData.isUnique) {
                            isUnique = true;
                        } else {
                            counter++;
                        }
                    }
                }

                setPlaceholderLinkId(linkId);
                setInputValue(linkId);
                setIsValid(true);
                setIsLoading(false);
            } catch (error) {
                console.error('Error generating placeholder link ID:', error);
                setIsLoading(false);
                navigate(GENERAL_ROUTES.ERROR);
            }
        };

        checkOrGeneratePlaceholderLinkId();
    }, [displayName, navigate]);
    // Check if link ID is valid
    const checkLinkId = useCallback(async (linkId) => {
        if (!linkId) {
            setIsValid(false);
            setErrorMessage('Link ID cannot be empty');
            return;
        }

        // Check if linkId contains only valid characters
        const validPattern = /^[a-z0-9-]+$/;
        if (!validPattern.test(linkId)) {
            setIsValid(false);
            setErrorMessage('Link ID can only contain lowercase letters, numbers, and hyphens');
            return;
        }

        try {
            setIsChecking(true);
            const response = await fetchWithToken('/check-link-unique', {
                method: 'POST',
                body: JSON.stringify({ linkId }),
            });

            const data = await response.json();

            if (data.isUnique) {
                setIsValid(true);
                setErrorMessage('');
            } else {
                setIsValid(false);
                setErrorMessage('This link ID is already taken');
            }
        } catch (error) {
            console.error('Error checking link ID:', error);
            setIsValid(false);
            setErrorMessage('Error checking link ID');
        } finally {
            setIsChecking(false);
        }
    },
        []
    );

    // Debounce function for input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (inputValue) {
                checkLinkId(inputValue);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [inputValue, checkLinkId]);

    const handleChange = (event) => {
        const value = event.target.value.toLowerCase();
        setInputValue(value);
        // We'll validate after the debounce timeout
        setIsValid(null);
    };

    const canGoBack = signupMethod === 'google';

    const handleBack = async () => {
        if (!canGoBack) return;
        setIsLoading(true);
        try {
            await updateOnboardingStage('enter-display-name');
            setOnboardingStage('enter-display-name');
            navigate(ONBOARDING_ROUTES.ENTER_DISPLAY_NAME);
        } catch (error) {
            console.error('Error going back:', error);
            navigate(GENERAL_ROUTES.ERROR);
        }
        setIsLoading(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            // Update the user's linkId
            const response = await fetchWithToken('/update-link-id', {
                method: 'PATCH',
                body: JSON.stringify({ linkId: inputValue }),
            });

            if (!response.ok) {
                if (response.status === 422) {
                    setErrorMessage('Custom link violates our content policy. Please choose another one.');
                    setIsLoading(false);
                    return;
                } else {
                    throw new Error('Failed to update display name.');
                }
            }

            setLinkId(inputValue);

            // WHILE CRYPTO IS INACTIVE, SKIP 'SELECT MONEY TYPE' SCREEN
            let nextStage;
            let nextRoute;

            if (stripeOnboardingStatus === 'complete') {
                nextStage = 'complete';
                nextRoute = GENERAL_ROUTES.HOME;
            } else {
                nextStage = 'fiat-onboarding';
                nextRoute = ONBOARDING_ROUTES.FIAT_ONBOARDING;
            }

            await updateOnboardingStage(nextStage);
            setOnboardingStage(nextStage);
            setIsLoading(false);
            navigate(nextRoute);

            // WHEN CRYPTO IS ACTIVE AGAIN:
            // await updateOnboardingStage('select-money-type');
            // setOnboardingStage('select-money-type');
            // navigate(ONBOARDING_ROUTES.SELECT_MONEY_TYPE);
        } catch (error) {
            console.error('Failed to update link ID:', error);
            setIsLoading(false);
            navigate(GENERAL_ROUTES.ERROR);
        }
    };

    if (isLoading) return <Loading />;

    return (
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh)]">
            <div className="flex flex-col items-center">
                <form onSubmit={handleSubmit} className="flex flex-col items-center space-y-5 w-full max-w-lg">
                    <label className="text-white text-center">
                        Choose a link for your tipping page:
                    </label>
                    <div className="relative w-72 flex items-center">
                        <p className="text-white font-semibold mr-1">tipl.co/</p>
                        <input
                            type="text"
                            name="linkId"
                            value={inputValue}
                            onChange={handleChange}
                            placeholder={placeholderLinkId}
                            className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-black text-center ${isValid === true ? 'border-green-500 focus:ring-green-500' :
                                isValid === false ? 'border-red-500 focus:ring-red-500' :
                                    'border-gray-300 focus:ring-blue-500'
                                }`}
                        />
                        {isValid === true && !isChecking && (
                            <FaCheckCircle className="h-5 w-5 text-green-500 absolute top-1/2 right-2 transform -translate-y-1/2" />
                        )}
                        {isValid === false && !isChecking && (
                            <FaExclamationCircle className="h-5 w-5 text-red-500 absolute top-1/2 right-2 transform -translate-y-1/2" />
                        )}
                    </div>
                    {errorMessage && (
                        <p className="text-red-500 text-sm text-center px-2">{errorMessage}</p>
                    )}
                    <div className="flex space-x-4">
                        {canGoBack && (
                            <button
                                type="button"
                                onClick={handleBack}
                                className="font-semibold bg-white text-tipl-teal px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300 w-24"
                                disabled={isLoading}
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="submit"
                            className={`${isValid ? 'bg-orange-200 text-black hover:bg-orange-300' : 'bg-gray-400 text-white cursor-not-allowed'
                                } w-24 font-semibold px-4 py-2 rounded-md transition duration-300`}
                            disabled={!isValid || isLoading || isChecking}
                        >
                            Next
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnterLinkId;
